import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Form, { ErrorMessage, Field, HelperMessage, RequiredAsterisk } from '@atlaskit/form';
import Button from '@atlaskit/button/new';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Select, { type ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { useFlags } from '@atlaskit/flag/flag-provider';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import { submitSurveyFeedback } from '../submit-feedback';
import {
	useDataFormatOptions,
	useDataFormatOptionsFromSource,
} from '../data-format-options/data-format-options';
import { getImportSourceEnum } from '../sources';

import { modalFormMessages } from './messages';

interface Option {
	label: string;
	value: string;
}

interface FeedbackModalProps {
	isOpen: boolean;
	closeModal: () => void;
	importSource: string;
	importDate: Date;
	contentId: string;
	onFeedbackSubmit: () => void;
}

export const FeedbackModal = ({
	isOpen,
	closeModal,
	importSource,
	importDate,
	contentId,
	onFeedbackSubmit,
}: FeedbackModalProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const importSourceEnum = getImportSourceEnum(importSource);
	const NOTION_DATA_FORMAT_OPTIONS = useDataFormatOptions();
	const DATA_FORMAT_OPTIONS = useDataFormatOptionsFromSource(importSourceEnum);

	if (!importSourceEnum && fg('cc_importer_axp_enable_miro_import_bulk_feedback')) {
		return null;
	}

	const onSubmit = (data: any) => {
		if (!data['data-format'] || data['data-format'].length === 0) {
			return {
				'data-format': intl.formatMessage(modalFormMessages.selectDataFormatError),
			};
		}

		submitSurveyFeedback(
			createAnalyticsEvent,
			'importedPageFeedbackSurveyModal',
			importSourceEnum ?? importSource,
			importDate,
			contentId,
			'negative', // Modal only shown for negative feedback
			data['data-format'].map((option: Option) => option.value),
			data['tell-us-about-your-experience'],
		);
		closeModal();
		onFeedbackSubmit();
		showFlag({
			isAutoDismiss: true,
			title: intl.formatMessage(modalFormMessages.submittedTitle),
			description: intl.formatMessage(modalFormMessages.submittedDescription),
			icon: <StatusSuccessIcon color={token('color.icon.success')} label="" />,
		});
	};

	return (
		<>
			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal} width="large" testId="import-feedback-modal">
						<Form onSubmit={onSubmit}>
							{({ formProps }) => (
								<form {...formProps} id="feedback-modal-form">
									<ModalHeader hasCloseButton>
										<ModalTitle>
											<FormattedMessage {...modalFormMessages.modalTitle} />
										</ModalTitle>
									</ModalHeader>
									<ModalBody>
										<Stack space="space.150">
											<Text>
												<FormattedMessage {...modalFormMessages.requiredFieldsAsterisk} />
												<RequiredAsterisk />
											</Text>
											<Text>
												<FormattedMessage
													{...modalFormMessages.feedbackText}
													values={{
														source: importSourceEnum,
													}}
												/>
											</Text>
											<Field<ValueType<Option, true>>
												name="data-format"
												label={<FormattedMessage {...modalFormMessages.dataFormatLabel} />}
												isRequired
												aria-required
												testId="data-format-select-field"
											>
												{({ fieldProps: { id, ...rest }, error }) => (
													<Fragment>
														<Select<Option, true>
															inputId={id}
															{...rest}
															options={
																fg('cc_importer_axp_enable_miro_import_bulk_feedback')
																	? DATA_FORMAT_OPTIONS
																	: NOTION_DATA_FORMAT_OPTIONS
															}
															menuPosition="fixed"
															isClearable
															isMulti
															clearControlLabel={intl.formatMessage(
																modalFormMessages.dataFormatClearControlLabel,
															)}
															testId="data-format-select-dropdown"
														/>
														{error && <ErrorMessage>{error}</ErrorMessage>}
														<HelperMessage>
															<FormattedMessage {...modalFormMessages.dataFormatHelperText} />
														</HelperMessage>
													</Fragment>
												)}
											</Field>

											<Field
												label={
													<FormattedMessage {...modalFormMessages.tellUsAboutYourExperience} />
												}
												name="tell-us-about-your-experience"
												testId="tell-us-about-your-experience-field"
												isRequired
												aria-required
											>
												{({ fieldProps }: any) => (
													<Fragment>
														<TextArea
															{...fieldProps}
															placeholder={intl.formatMessage(
																modalFormMessages.tellUsAboutYourExperiencePlaceholder,
															)}
															testId="tell-us-about-your-experience-textarea"
														/>
													</Fragment>
												)}
											</Field>
										</Stack>
									</ModalBody>
									<ModalFooter>
										<Button
											appearance="subtle"
											onClick={closeModal}
											testId="feedback-modal-close-button"
										>
											<FormattedMessage {...modalFormMessages.closeButton} />
										</Button>
										<Button
											appearance="primary"
											type="submit"
											testId="feedback-modal-submit-button"
										>
											<FormattedMessage {...modalFormMessages.feedbackButton} />
										</Button>
									</ModalFooter>
								</form>
							)}
						</Form>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};
